import React, { useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FunctionField, useDataProvider, useNotify } from 'react-admin';
import Box from '@material-ui/core/Box';
import { Button, Card, Chip, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import moment from 'moment';
import { keyBy } from 'lodash';

import { Select } from 'components/form';
import FilterDateInput from 'components/inputs/FilterDateInput';
import { Dialog } from 'components/common';
import { Datagrid } from 'components/list';
import { DateField } from 'components/fields';
import { UserDetails } from '../types';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },

  toolbarCard: {
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(2),
    overflow: 'visible',
    padding: theme.spacing(2),
  },

  toolbarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },

  title: {
    fontSize: 18,
    fontWeight: 600,
  },

  meta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: theme.spacing(0.5),
  },

  actions: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '32px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },

  filters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    margin: theme.spacing(-1),
  },

  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    margin: theme.spacing(1, 0, 1, 2),

    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },

  severity: {
    width: theme.spacing(20),
    margin: theme.spacing(1),
  },

  pageSize: {
    width: theme.spacing(20),
    margin: theme.spacing(1),
  },

  dateRange: {
    margin: theme.spacing(1),
  },

  grid: {
    marginTop: theme.spacing(2),
  },

  gridCard: {
    backgroundColor: theme.palette.common.white,
  },

  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 1),
  },

  statusGroup: {
    display: 'flex',
    alignItems: 'center',

    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },

  loadingState: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,

    '& > *:first-child': {
      marginRight: theme.spacing(1),
    },
  },

  staleChip: {
    backgroundColor: '#FFF4E5',
    color: '#9A5B00',
    fontWeight: 600,
  },

  emptyState: {
    padding: theme.spacing(8, 2),
    textAlign: 'center',
  },

  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
  },

  emptyMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },

  logsTable: {
    tableLayout: 'fixed',
  },

  logCell: {
    overflow: 'hidden',
  },

  levelChip: {
    height: theme.spacing(3),
    minWidth: theme.spacing(7),
    backgroundColor: '#EEF7F0',
    color: '#1B7F3A',
    fontSize: 12,
    fontWeight: 600,
  },

  monoText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
  },

  contentPreview: {
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },

  bottomActions: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },

  detailsContent: {
    margin: 0,
    maxHeight: '70vh',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: 13,
    lineHeight: 1.5,
  },

  button: {
    minWidth: theme.spacing(12),
    textTransform: 'none',
  },

  detailsTitle: {
    display: 'flex',
    alignItems: 'center',

    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },

  titleCopyButton: {
    minWidth: theme.spacing(8),
    textTransform: 'none',
  },
}));

const SEVERITY_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100].map((value) => ({
  value,
  label: String(value),
}));

const JSON_MESSAGE_MARKER = 'JSON:';

const getDefaultDateRange = () => ({
  from: moment().subtract(24, 'hours').toISOString(),
  to: moment().toISOString(),
});

const getSeverityLabel = (value: string) =>
  SEVERITY_OPTIONS.find((option) => option.value === value)?.label || value;

const formatDateTime = (value: string | number | null) =>
  value ? moment(value).format('MMM D, YYYY, h:mm A') : 'Any time';

const formatDateRange = (from: string | number | null, to: string | number | null) =>
  `${formatDateTime(from)} - ${formatDateTime(to)}`;

const formatQuerySummary = (queryParams: QueryParams | null, count: number) => {
  if (!queryParams) {
    return 'No search run';
  }

  return `${count} logs | ${getSeverityLabel(queryParams.severity)} | ${formatDateRange(
    queryParams.from,
    queryParams.to
  )}`;
};

const getMessagePreview = (text: Record<string, any>) => {
  const message = text?.message;
  const messageValue = Array.isArray(message) ? message[0] : message;

  if (typeof messageValue !== 'string') {
    return '---';
  }

  return messageValue;
};

const formatMessageDetails = (message: any) => {
  if (Array.isArray(message)) {
    return message.map(formatMessageDetails);
  }

  if (typeof message !== 'string') {
    return message;
  }

  const markerIndex = message.indexOf(JSON_MESSAGE_MARKER);

  if (markerIndex === -1) {
    return message;
  }

  const messagePrefix = message.slice(0, markerIndex).trim();
  const jsonContent = message.slice(markerIndex + JSON_MESSAGE_MARKER.length).trim();

  if (!jsonContent) {
    return message;
  }

  try {
    return {
      messagePrefix,
      parsedJson: JSON.parse(jsonContent),
      rawMessage: message,
    };
  } catch (error) {
    return message;
  }
};

const formatLogForDetails = (log: AuditLogCloudwatchDTO | null) => {
  if (!log?.text || !Object.prototype.hasOwnProperty.call(log.text, 'message')) {
    return log;
  }

  return {
    ...log,
    text: {
      ...log.text,
      message: formatMessageDetails(log.text.message),
    },
  };
};

const formatLogDetails = (log: AuditLogCloudwatchDTO | null) => {
  try {
    const formattedLog = JSON.stringify(formatLogForDetails(log), null, 2);

    return formattedLog === undefined ? 'No log details' : formattedLog;
  } catch (error) {
    return String(log);
  }
};

interface AuditLogCloudwatchDTO {
  id: string;
  createdAt: string;
  severity: string;
  text: Record<string, any>;
  level: string;
  source: string;
  loggerName: string;
  logId: string;
  awsTraceId: string | null;
}

interface QueryParams {
  severity: string;
  tenantId: string;
  billingId: number;
  from: number;
  to: number;
  pageSize: number;
}

interface DateRange {
  from: string | null;
  to: string | null;
}

interface GetLogsOptions {
  appendResults?: boolean;
  token?: string | null;
}

interface Props {
  user: UserDetails;
}

const CloudWatchLogsTab = ({ user }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [severity, setSeverity] = useState(SEVERITY_OPTIONS[0].value);
  const [pageSize, setPageSize] = useState(20);
  const [defaultDateRange] = useState<DateRange>(getDefaultDateRange);
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<AuditLogCloudwatchDTO[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLogCloudwatchDTO | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState<QueryParams | null>(null);
  const lastQueryParamsRef = useRef<QueryParams | null>(null);
  const dateRangeOptions = useMemo(
    () => [
      {
        label: 'Last 24 hours',
        value: defaultDateRange,
      },
    ],
    [defaultDateRange]
  );

  const tenantId = user?.id;
  const billingId = user?.billingUserId;
  const buttonDisabled = loading || !tenantId || !billingId || !dateRange.from || !dateRange.to;
  const showLoadMore = !!nextToken && !!lastQueryParamsRef.current;
  const filtersChangedSinceSearch = logs.length > 0 && !lastQueryParamsRef.current && !loading;
  const selectedFilterSummary = `${getSeverityLabel(severity)} | ${formatDateRange(
    dateRange.from,
    dateRange.to
  )} | ${pageSize} per page`;
  const resultSummary = formatQuerySummary(lastSearchParams, logs.length);

  const runCloudWatchLogsFilter = async (
    queryParams: QueryParams,
    appendResults = false,
    token?: string | null
  ) => {
    const { data } = await dataProvider.get('cloudWatchLogsFilter', {
      ...queryParams,
      ...(token ? { nextToken: token } : {}),
    });

    if (!Array.isArray(data?.items)) {
      throw new Error('Unexpected filter response');
    }

    setLogs((currentLogs) => (appendResults ? [...currentLogs, ...data.items] : data.items));
    setNextToken(data.nextToken || null);
    lastQueryParamsRef.current = queryParams;
    setLastSearchParams(queryParams);
    setHasSearched(true);
  };

  const handleGetLogs = async ({ appendResults = false, token = null }: GetLogsOptions = {}) => {
    if (!tenantId || !billingId) {
      return;
    }

    if (!appendResults && (!dateRange.from || !dateRange.to)) {
      notify('CloudWatch date range is required', 'warning');
      return;
    }

    const queryParams =
      appendResults && lastQueryParamsRef.current
        ? lastQueryParamsRef.current
        : {
            severity,
            tenantId,
            billingId,
            from: moment(dateRange.from).valueOf(),
            to: moment(dateRange.to).valueOf(),
            pageSize: Number(pageSize),
          };

    try {
      setLoading(true);

      if (!appendResults) {
        setLogs([]);
        setNextToken(null);
        lastQueryParamsRef.current = null;
      }

      await runCloudWatchLogsFilter(queryParams, appendResults, token);
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!lastQueryParamsRef.current || !nextToken) {
      return;
    }

    await handleGetLogs({ appendResults: true, token: nextToken });
  };

  const handleOpenLogDetails = (_id: string, _basePath: string, record: AuditLogCloudwatchDTO) => {
    if (!record) {
      return;
    }

    setSelectedLog(record);
  };

  const handleCloseLogDetails = () => {
    setSelectedLog(null);
  };

  const handleCopyLogDetails = async () => {
    if (!selectedLog) {
      notify('No log details to copy', 'warning');
      return;
    }

    if (!window.navigator?.clipboard?.writeText) {
      notify('Copy to clipboard is not available', 'warning');
      return;
    }

    try {
      await window.navigator.clipboard.writeText(formatLogDetails(selectedLog));
      notify('CloudWatch log details copied');
    } catch (error) {
      notify('Failed to copy CloudWatch log details', 'warning');
    }
  };

  return (
    <Box className={classes.root}>
      <Card className={classes.toolbarCard}>
        <Box className={classes.toolbarHeader}>
          <Box>
            <Typography className={classes.title}>CloudWatch logs</Typography>
            <Typography className={classes.meta}>{selectedFilterSummary}</Typography>
          </Box>
        </Box>
        <Form
          initialValues={{ severity, pageSize, date: dateRange }}
          onSubmit={() => {}}
          render={({ form }) => (
            <Box className={classes.actions}>
              <Box className={classes.filters}>
                <Box className={classes.severity}>
                  <Select
                    name="severity"
                    label="Severity"
                    options={SEVERITY_OPTIONS}
                    inputProps={{
                      onChange: (event: any) => {
                        form.change('severity', event.target.value);
                        setSeverity(event.target.value);
                      },
                    }}
                  />
                </Box>
                <Box className={classes.dateRange}>
                  <Field
                    name="date"
                    render={({ input, meta }) => (
                      <FilterDateInput
                        input={{
                          ...input,
                          onChange: (value: DateRange) => {
                            input.onChange(value);
                            setDateRange(value);
                            setNextToken(null);
                            lastQueryParamsRef.current = null;
                          },
                        }}
                        meta={meta}
                        label="Date"
                        options={dateRangeOptions}
                      />
                    )}
                  />
                </Box>
                <Box className={classes.pageSize}>
                  <Select
                    name="pageSize"
                    label="Page size"
                    options={PAGE_SIZE_OPTIONS}
                    inputProps={{
                      onChange: (event: any) => {
                        const nextPageSize = Number(event.target.value);

                        form.change('pageSize', nextPageSize);
                        setPageSize(nextPageSize);
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box className={classes.toolbarActions}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={buttonDisabled}
                  onClick={() => handleGetLogs()}>
                  {loading ? 'Loading...' : 'Get logs'}
                </Button>
                {showLoadMore && (
                  <Button
                    variant="outlined"
                    className={classes.button}
                    disabled={loading}
                    onClick={handleLoadMore}>
                    Load more
                  </Button>
                )}
              </Box>
            </Box>
          )}
        />
      </Card>

      <Box className={classes.grid}>
        <Card className={classes.gridCard}>
          <Box className={classes.resultsHeader}>
            <Box>
              <Typography className={classes.title}>Results</Typography>
              <Typography className={classes.meta}>{resultSummary}</Typography>
            </Box>
            <Box className={classes.statusGroup}>
              {loading && (
                <Box className={classes.loadingState}>
                  <CircularProgress size={16} />
                  <span>Loading</span>
                </Box>
              )}
              {filtersChangedSinceSearch && (
                <Chip size="small" label="Filters changed" className={classes.staleChip} />
              )}
            </Box>
          </Box>

          {loading && !logs.length ? (
            <Box className={classes.emptyState}>
              <CircularProgress size={28} />
              <Typography className={classes.emptyMeta}>Loading logs</Typography>
            </Box>
          ) : logs.length ? (
            <>
              <Datagrid
                loaded
                classes={{
                  table: classes.logsTable,
                  rowCell: classes.logCell,
                }}
                data={keyBy(logs, 'id')}
                ids={logs.map(({ id }) => id)}
                resource="cloudWatchLogsFilter"
                rowClick={handleOpenLogDetails}
                rowStyle={() => ({ cursor: 'pointer' })}
                widths={['190px', '92px', '220px', '260px', 'auto']}
                currentSort={{ field: 'createdAt', order: 'DESC' }}>
                <DateField source="createdAt" label="Timestamp" withTime sortable={false} />
                <FunctionField
                  source="level"
                  label="Level"
                  sortable={false}
                  render={(record) => (
                    <Chip
                      size="small"
                      label={record?.level || '---'}
                      className={classes.levelChip}
                    />
                  )}
                />
                <FunctionField
                  source="source"
                  label="Source"
                  sortable={false}
                  render={(record) => (
                    <span className={classes.monoText}>{record?.source || '---'}</span>
                  )}
                />
                <FunctionField
                  source="logId"
                  label="Log Id"
                  sortable={false}
                  render={(record) => (
                    <span className={classes.monoText}>{record?.logId || '---'}</span>
                  )}
                />
                <FunctionField
                  source="content"
                  label="Content"
                  sortable={false}
                  render={(record) => (
                    <span className={classes.contentPreview}>
                      {getMessagePreview(record?.text)}
                    </span>
                  )}
                />
              </Datagrid>
              {showLoadMore && (
                <Box className={classes.bottomActions}>
                  <Button
                    variant="outlined"
                    className={classes.button}
                    disabled={loading}
                    onClick={handleLoadMore}>
                    Load more
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyTitle}>
                {hasSearched ? 'No logs returned' : 'No logs loaded'}
              </Typography>
              <Typography className={classes.emptyMeta}>
                {hasSearched
                  ? 'Current filters returned no CloudWatch events.'
                  : 'No CloudWatch events loaded yet.'}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>
      {selectedLog && (
        <Dialog
          open={Boolean(selectedLog)}
          onCancel={handleCloseLogDetails}
          maxWidth="lg"
          fullWidth
          title={
            <Box className={classes.detailsTitle}>
              <span>CloudWatch log details</span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className={classes.titleCopyButton}
                onClick={handleCopyLogDetails}>
                Copy
              </Button>
            </Box>
          }
          cancelText="Close">
          <pre className={classes.detailsContent}>{formatLogDetails(selectedLog)}</pre>
        </Dialog>
      )}
    </Box>
  );
};

export default CloudWatchLogsTab;
